/**
 * @fileoverview `createFetchSeam` — the canonical lazy `globalThis.fetch` seam.
 * @module @puffin-core/api-client/createFetchSeam
 *
 * RES-CLIENT-001 `Q-TRANSPORT-DISEAM` (owner-ruled 2026-08-30: converge them).
 *
 * ## What this replaces, and why it was not merely duplication
 *
 * Eleven hand-rolled copies of `(...args) => globalThis.fetch(...args)` stood across the fleet —
 * ten app-level (`MarketingWebsiteApp.jsx` in nine web apps plus purchase-web's `TenantSiteApp.jsx`)
 * and one in `@puffin-core/portal-analytics`. `web/no-bespoke-http-transport` counts every one as a
 * transport, correctly: each IS a construction of an HTTP transport. So the ledger could not reach
 * zero while they stood, and the rule's stated remedy — "adopt the canonical client" — did not fit,
 * because these are not clients. They are a lazy binding to the platform's own `fetch`.
 *
 * A dependency-injection seam is the right shape here. What was wrong was having eleven of it.
 *
 * ## The property every copy depended on and none of them stated
 *
 * **Resolution must be LAZY.** Each copy carried the comment *"Lazy fetch arrow keeps jsdom-mode
 * tests hermetic"*, which is the consequence, not the mechanism: `globalThis.fetch` is read when
 * the seam is CALLED, not when it is constructed, so a suite that installs a stub after module load
 * still wins. A seam that captured `globalThis.fetch` at construction would pass a smoke test and
 * silently break every hermetic suite that stubs late. That property is pinned by a test rather
 * than by this paragraph.
 *
 * ## The half the fleet did NOT have, folded in
 *
 * The ten inline copies, called with no `globalThis.fetch` present, throw
 * `globalThis.fetch is not a function` — a `TypeError` naming neither the caller nor the cause.
 * `portal-analytics` was the only site that answered properly, with an explicit rejection. That
 * answer is now the fleet's: an absent global rejects with a NAMED error, asynchronously, because
 * callers attach `.catch` rather than wrapping the call site in `try`/`catch` — a synchronous throw
 * would escape their handlers.
 *
 * ## Why the caller can NAME itself (`label`)
 *
 * Added 2026-08-30 by the facade-adoption arc, when `portal-analytics` — the eleventh seam, and the
 * one whose answer this factory adopted — was finally converged. Its hand-rolled rejection named
 * the factory the caller had actually called (`createPortalAnalyticsService`); this seam names
 * itself, a shared utility the caller never mentioned. Converging without `label` would therefore
 * have traded a WORSE diagnostic for a shared implementation, and Gen-0 does not lose a fact to
 * gain a factory. So the caller's identity is a parameter.
 *
 * The label is the WHO; the sentence after it is the WHAT and never varies. A second `hint` option
 * for the caller's REMEDY was considered and rejected: exactly one of the eleven sites would have
 * passed one, which is a branch nobody else can test truthfully — and the remedy those callers
 * document (`portal-analytics`' `fetcher` option) is one hop from the error, on the option itself.
 *
 * @param {Object} [options] - Options.
 * @param {string} [options.label='createFetchSeam'] - The name reported as the source of an
 *   absent-global rejection. Omit it and the seam names itself; supply it and it names you.
 * @returns {(...args: unknown[]) => Promise<Response>} A fetch-shaped function.
 * @throws {TypeError} When `label` is supplied but is not a non-empty string — see the guard below.
 *
 * @example
 * const cmsClient = createMarketingCmsClient({ appKey: APP_KEY, fetch: createFetchSeam() });
 * @example <caption>naming the caller, so the rejection says who needed the fetch</caption>
 * const fetcher = createFetchSeam({ label: 'createPortalAnalyticsService' });
 */
export function createFetchSeam({ label } = {}) {
  // 🔴 Guard at CONSTRUCTION, where the mistake is — not at the first absent-fetch call, which may
  // never happen in the environment that shipped the bug. An unrenderable label fails SILENTLY
  // otherwise: `''` yields ": no global fetch…" and `123` yields "123: no global fetch…", both
  // strictly worse than the default and neither one anything a reader would recognise as a defect.
  // Same reasoning as `createApiFacade`'s transport guard: a silent no-op is the worst outcome.
  if (label !== undefined && (typeof label !== 'string' || label.length === 0)) {
    throw new TypeError('createFetchSeam: `label` must be a non-empty string when supplied.');
  }
  const source = label ?? 'createFetchSeam';

  return (...args) => {
    const impl = globalThis.fetch;
    if (typeof impl !== 'function') {
      // Rejection, never a synchronous throw — see the docblock. Named so a failure in a prerender
      // or a Node context without `fetch` says what is missing instead of surfacing as an
      // anonymous TypeError three frames inside whichever client borrowed the seam.
      return Promise.reject(
        new Error(`${source}: no global fetch available in this environment`),
      );
    }
    return impl(...args);
  };
}
